import { AuthenticatedVisualizationActionIframe } from "@app/components/assistant/conversation/actions/AuthenticatedVisualizationActionIframe";
import { DEFAULT_FRAME_PANEL_SIZE } from "@app/components/assistant/conversation/constant";
import { ConversationSidePanelHeader } from "@app/components/assistant/conversation/ConversationSidePanelHeader";
import { CenteredState } from "@app/components/assistant/conversation/interactive_content/CenteredState";
import { FrameCodeView } from "@app/components/assistant/conversation/interactive_content/frame/FrameCodeView";
import { FrameDraftControls } from "@app/components/assistant/conversation/interactive_content/frame/FrameDraftControls";
import { FrameDraftModeBorder } from "@app/components/assistant/conversation/interactive_content/frame/FrameDraftModeBorder";
import { FrameToolbar } from "@app/components/assistant/conversation/interactive_content/frame/FrameToolbar";
import { PreviewActionButtons } from "@app/components/assistant/conversation/interactive_content/frame/PreviewActionButtons";
import { useFrameEditSession } from "@app/components/assistant/conversation/interactive_content/frame/useFrameEditSession";
import { useSidePanelFrameTracking } from "@app/components/assistant/conversation/interactive_content/useFrameTracking";
import { useSidePanelFullScreen } from "@app/components/assistant/conversation/useSidePanelFullScreen";
import { ConfirmContext } from "@app/components/Confirm";
import { useVisualizationRevert } from "@app/hooks/conversations";
import {
  useSendApiErrorNotification,
  useSendNotification,
} from "@app/hooks/useNotification";
import { useAuth, useFeatureFlags } from "@app/lib/auth/AuthContext";
import { clientFetch } from "@app/lib/egress/client";
import {
  useFileContent,
  useFileContentByUrl,
  useFileMetadata,
  useShareInteractiveContentFile,
} from "@app/lib/swr/files";
import { useEditFrameText, useFramePermissions } from "@app/lib/swr/frames";
import { usePodFiles } from "@app/lib/swr/pods";
import { useSpaceInfo } from "@app/lib/swr/spaces";
import { getErrorFromResponse } from "@app/lib/swr/swr";
import { useIsMobile } from "@app/lib/swr/useIsMobile";
import type { ConversationWithoutContentType } from "@app/types/assistant/conversation";
import type { LightWorkspaceType } from "@app/types/user";
import { Button, Cube01, Spinner } from "@dust-tt/sparkle";
import { Trans, useLingui } from "@lingui/react/macro";
import { useCallback, useContext, useMemo, useRef, useState } from "react";

interface FrameRendererProps {
  conversation: ConversationWithoutContentType;
  fileId: string;
  projectId: string | null;
  owner: LightWorkspaceType;
  lastEditedByAgentConfigurationId?: string;
  contentHash?: string;
  renderMode: "legacy" | "v2";
}

export function FrameRenderer({
  conversation,
  fileId,
  projectId,
  owner,
  lastEditedByAgentConfigurationId,
  contentHash,
  renderMode,
}: FrameRendererProps) {
  const { t } = useLingui();
  const { vizUrl } = useAuth();
  const isMobile = useIsMobile();
  const [isLoading, setIsLoading] = useState(false);

  const { spaceInfo: projectInfo, isSpaceInfoLoading } = useSpaceInfo({
    workspaceId: owner.sId,
    spaceId: conversation.spaceId ?? projectId ?? null,
  });

  const isFrameInPod = Boolean(
    projectId && projectInfo?.kind === "project" && !projectInfo.archivedAt
  );

  const projectSaveState = useMemo(() => {
    if (!projectInfo && isSpaceInfoLoading) {
      return "unknown";
    }
    if (
      !conversation.spaceId ||
      !projectInfo ||
      projectInfo.kind !== "project"
    ) {
      return "unsupported";
    }
    if (!projectId) {
      return "supported";
    }

    return "saved";
  }, [conversation.spaceId, projectId, projectInfo, isSpaceInfoLoading]);

  const { files: projectFiles } = usePodFiles({
    owner,
    podId: projectId ?? "",
    disabled: !isFrameInPod,
  });

  const framePath = useMemo(() => {
    const entry = projectFiles.find(
      (file) => !file.isDirectory && file.fileId === fileId
    );
    return entry?.path ?? null;
  }, [fileId, projectFiles]);

  const iframeRef = useRef<HTMLIFrameElement>(null);

  // The space to resolve `project/` file paths inside the viz.
  // Priority: explicit project the frame was saved to -> the conversation's own project space
  // (a conversation can belong to a project space even before the frame is saved there).
  const frameSpaceId = projectId ?? conversation.spaceId ?? null;

  const {
    canEnterFullScreen,
    isFullScreen,
    enterFullScreen,
    exitFullScreen,
    closePanel: onClosePanel,
  } = useSidePanelFullScreen(DEFAULT_FRAME_PANEL_SIZE);

  useSidePanelFrameTracking({
    frameId: fileId,
    location: { type: "conversation", id: conversation.sId },
    uiElement: "conversation_side_panel",
    isFullScreen,
    canEnterFullScreen,
  });

  const { fileContent, error, mutateFileContent, isFileContentLoading } =
    useFileContent({
      fileId,
      owner,
      cacheKey: contentHash,
      // The conversation panel is the one surface that shows editors the latest publication
      // (a pending draft); the server still serves live to callers who cannot write the Frame.
      publication: "latest",
    });

  const { fileMetadata, mutateFileMetadata } = useFileMetadata({
    fileId,
    owner,
  });

  const { fileShare } = useShareInteractiveContentFile({
    fileId,
    owner,
    cacheKey: contentHash,
  });

  const sendNotification = useSendNotification();
  const sendApiErrorNotification = useSendApiErrorNotification();
  const confirm = useContext(ConfirmContext);
  const [isSavingToProject, setIsSavingToProject] = useState(false);

  const { handleVisualizationRevert } = useVisualizationRevert({
    workspaceId: owner.sId,
    conversationId: conversation.sId,
  });

  const [showCode, setShowCode] = useState(false);

  // A legacy Frame renders its own source, so `fileContent` is the code. A Frames v2 package
  // renders a built bundle, so its sources are fetched separately, and only once shown.
  const frameSourceBaseUrl = `/api/w/${owner.sId}/frames/${encodeURIComponent(fileId)}/source`;
  const frameSourceUrl = contentHash
    ? `${frameSourceBaseUrl}?v=${encodeURIComponent(contentHash)}`
    : frameSourceBaseUrl;
  const {
    fileContent: frameSource,
    isNotFound: isFrameSourceNotFound,
    isFileContentLoading: isFrameSourceLoading,
    fileContentError: frameSourceError,
    mutateFileContent: mutateFrameSource,
  } = useFileContentByUrl({
    url: frameSourceUrl,
    disabled: renderMode !== "v2" || !showCode,
  });

  const {
    canWriteFrameSource,
    packageRoot,
    hasFrameFunctions,
    isFramePermissionsLoading,
    hasFramePermissions,
    draftPublicationId,
    draftMode,
    livePublicationId,
    mutateFramePermissions,
  } = useFramePermissions({
    owner,
    frameId: fileId,
    publication: "latest",
    cacheKey: contentHash,
    disabled: renderMode !== "v2",
  });
  const framePackageRoot =
    framePath && framePath.includes("/")
      ? framePath.slice(0, framePath.lastIndexOf("/"))
      : packageRoot;
  // useFile("./…") resolves against framePackageRoot on first fetch and never retries. Wait until
  // permissions (packageRoot) have loaded so the iframe does not mount with a null root. A refetch
  // for a new contentHash keeps the previous permissions, so it does not wait again.
  const isFramePathPending =
    renderMode === "v2" && isFramePermissionsLoading && !hasFramePermissions;
  const editFrameText = useEditFrameText({
    owner,
    fileId,
    conversationId: conversation.sId,
  });
  const isEditable = renderMode === "legacy" || canWriteFrameSource;
  // Frames v2 source writers edit in a Preview|Edit session with batch Save. Legacy Frames keep
  // the always-on double-click + blur-save path.
  const canEditV2 = renderMode === "v2" && isEditable;
  const { hasFeature } = useFeatureFlags();
  const isDraftMode = canEditV2 && draftMode;
  const editSession = useFrameEditSession({
    owner,
    fileId,
    conversationId: conversation.sId,
    iframeRef,
    mutateFileContent,
    onSaved: mutateFramePermissions,
  });
  // Remount via React key only (identifier stays stable so Next.js keeps one /content URL):
  // on a new contentHash (agent publish / interactive content) and, for v2, after Save/discard/reload.
  const vizInstanceKey = `${
    contentHash
      ? `viz-${contentHash}`
      : `viz-${fileId}-${framePath ?? packageRoot ?? ""}`
  }${renderMode === "v2" ? `-${editSession.contentRevision}` : ""}`;

  const handleEditText = useCallback(
    async (params: Parameters<typeof editFrameText>[0]) => {
      const result = await editFrameText(params);

      if (result.success) {
        try {
          await mutateFileContent();
        } catch {
          // The mutation already succeeded. Keep the inline edit and let the next reload fetch
          // the active publication rather than reporting a false save failure to the iframe.
        }
      }

      return result;
    },
    [editFrameText, mutateFileContent]
  );

  const reloadFile = async () => {
    setIsLoading(true);
    await mutateFileContent();
    if (renderMode === "v2") {
      // v2 renders a built bundle: remount so react-runner loads the refreshed content.
      editSession.remount();
    }
    setIsLoading(false);
  };

  // Discard moves the latest publication back to live without a new contentHash: refetch what the
  // panel shows, then remount the iframe on the live bundle. The panel shows its loading state
  // until every refresh is done, so the old draft never shows as the idle live Frame.
  const handleDraftDiscarded = async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        mutateFramePermissions(),
        mutateFileContent(),
        mutateFrameSource(),
      ]);
      editSession.remount();
    } finally {
      setIsLoading(false);
    }
  };

  const onRevert = () => {
    void handleVisualizationRevert({
      fileId,
      agentConfigurationId: lastEditedByAgentConfigurationId ?? "",
    });
  };

  const handleSaveToProject = useCallback(async () => {
    const projectIdToSave = conversation.spaceId;
    if (!projectIdToSave) {
      return;
    }

    const podName = projectInfo?.name ?? t`Pod`;
    const confirmed = await confirm({
      title: (
        <Trans>
          Save to <strong>{podName}</strong>?
        </Trans>
      ),
      message: (
        <>
          <div>
            <Trans>
              The Frame will be part of the Pod knowledge, and be able to be
              edited by any Pod member.
            </Trans>
          </div>
          <div>
            <Trans>This action cannot be undone.</Trans>
          </div>
        </>
      ),
      validateLabel: t`Save`,
      validateVariant: "primary",
    });
    if (!confirmed) {
      return;
    }
    setIsSavingToProject(true);
    try {
      const res = await clientFetch(
        `/api/w/${owner.sId}/files/${fileId}/save-in-project`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ projectId: projectIdToSave }),
        }
      );
      if (!res.ok) {
        const errorData = await getErrorFromResponse(res);
        sendApiErrorNotification({
          title: t`Failed to save to Pod`,
          error: errorData,
        });
        return;
      }
      sendNotification({
        type: "success",
        title: t`Saved to Pod`,
        description: t`Frame saved to "${podName}".`,
      });
      // Invalidate file metadata so parent and this component get updated projectId.
      await mutateFileMetadata();
    } catch (e) {
      sendNotification({
        type: "error",
        title: t`Failed to save to Pod`,
        description: e instanceof Error ? e.message : t`An error occurred`,
      });
    } finally {
      setIsSavingToProject(false);
    }
  }, [
    confirm,
    conversation.spaceId,
    fileId,
    mutateFileMetadata,
    owner.sId,
    projectInfo?.name,
    sendNotification,
    sendApiErrorNotification,
    t,
  ]);

  if (error) {
    const errorMessage = error.message;
    return (
      <div className="flex h-panel flex-col">
        <ConversationSidePanelHeader onClose={onClosePanel} />
        <CenteredState>
          <p className="text-warning-500">
            <Trans>Error loading file: {errorMessage}</Trans>
          </p>
        </CenteredState>
      </div>
    );
  }

  const savePodName = projectInfo?.name ?? t`unknown Pod`;

  return (
    <div className="flex h-panel flex-col">
      <ConversationSidePanelHeader onClose={onClosePanel}>
        <FrameToolbar
          owner={owner}
          fileId={fileId}
          iframeRef={iframeRef}
          fileContent={fileContent ?? null}
          fileName={fileMetadata?.fileName}
          contentType={fileMetadata?.contentType}
          contentHash={contentHash}
          showCode={showCode}
          onToggleCode={
            renderMode === "legacy" || canWriteFrameSource
              ? () => setShowCode(!showCode)
              : undefined
          }
          hasFrameFunctions={hasFrameFunctions}
          editSession={editSession}
          canEditV2={canEditV2}
          hideEditLabels={isMobile}
          podActions={
            isFrameInPod
              ? {
                  spaceId: projectId ?? "",
                  pinnedFramePath: projectInfo?.pinnedFramePath ?? null,
                  isEditor: projectInfo?.isEditor ?? false,
                  framePath,
                  fileName: fileMetadata?.fileName,
                  fileTabs: projectInfo?.frameTabs ?? [],
                  tabsOrder: projectInfo?.tabsOrder ?? [],
                }
              : null
          }
          trailing={
            <>
              {projectSaveState === "saved" && (
                <Button
                  icon={Cube01}
                  variant="ghost"
                  disabled={true}
                  label={isMobile ? undefined : t`Saved`}
                  tooltip={t`Saved in "${savePodName}"`}
                />
              )}
              {projectSaveState === "supported" && (
                <Button
                  icon={Cube01}
                  variant="ghost"
                  label={
                    isMobile
                      ? undefined
                      : isSavingToProject
                        ? t`Saving…`
                        : t`Save`
                  }
                  isLoading={isSavingToProject}
                  tooltip={t`Save to "${savePodName}"`}
                  onClick={handleSaveToProject}
                />
              )}
              {canEditV2 && (
                // Draft mode is turned on from the conversation panel only: the one surface that
                // shows drafts.
                <FrameDraftControls
                  owner={owner}
                  frameId={fileId}
                  canEnterDraftMode={hasFeature("frames_v2_drafts")}
                  draftMode={draftMode}
                  draftPublicationId={draftPublicationId}
                  livePublicationId={livePublicationId}
                  hideLabels={isMobile}
                  onDraftDiscarded={handleDraftDiscarded}
                  onDraftModeChanged={() => mutateFramePermissions()}
                />
              )}
            </>
          }
        />
      </ConversationSidePanelHeader>

      <div className="flex-1 overflow-hidden">
        {isLoading || isFramePathPending || isFileContentLoading ? (
          <Spinner />
        ) : (
          <>
            <FrameDraftModeBorder isDraftMode={isDraftMode}>
              {showCode ? (
                <FrameCodeView
                  code={
                    renderMode === "v2"
                      ? (frameSource ?? undefined)
                      : fileContent
                  }
                  isLoading={renderMode === "v2" && isFrameSourceLoading}
                  hasError={
                    renderMode === "v2" &&
                    (isFrameSourceNotFound || Boolean(frameSourceError))
                  }
                />
              ) : (
                <div className="relative h-full">
                  <AuthenticatedVisualizationActionIframe
                    agentConfigurationId={
                      fileMetadata?.useCaseMetadata
                        .lastEditedByAgentConfigurationId ?? ""
                    }
                    workspaceId={owner.sId}
                    vizUrl={vizUrl}
                    visualization={{
                      code: fileContent ?? "",
                      complete: true,
                      // Stable across revisions — contentHash belongs in `key` only.
                      // Putting it in identifier forced Next.js to recompile /content
                      // for every publish / content refresh.
                      identifier: `viz-${fileId}`,
                    }}
                    key={vizInstanceKey}
                    conversationId={conversation?.sId ?? null}
                    spaceId={frameSpaceId ?? undefined}
                    framePackageRoot={framePackageRoot}
                    frameId={renderMode === "v2" ? fileId : undefined}
                    frameFileId={fileId}
                    isInDrawer={true}
                    functionTarget="latest"
                    isEditable={isEditable}
                    stagedEditMode={canEditV2 ? editSession.mode : undefined}
                    onEditText={
                      !isEditable
                        ? undefined
                        : canEditV2
                          ? editSession.stageEdit
                          : handleEditText
                    }
                    ref={iframeRef}
                  />
                  {editSession.isSaving && (
                    <div
                      className="absolute inset-0 z-10 cursor-wait"
                      aria-busy="true"
                      aria-label={t`Publishing your changes...`}
                    />
                  )}
                </div>
              )}
            </FrameDraftModeBorder>
            <PreviewActionButtons
              lastEditedByAgentConfigurationId={
                lastEditedByAgentConfigurationId
              }
              hasPreviousVersion={(fileMetadata?.version ?? 0) > 1}
              onRevert={onRevert}
              isFullScreen={isFullScreen}
              exitFullScreen={exitFullScreen}
              enterFullScreen={enterFullScreen}
              shareUrl={fileShare?.shareUrl}
              reloadFile={reloadFile}
            />
          </>
        )}
      </div>
    </div>
  );
}
